import { useState, useCallback, useMemo } from 'react';
import { AttributeColumnRenderer, TableStyle, Attributes } from '@leverege/ui-attributes';
import { Dialog, Button, Flex, Text, Dialogs } from '@leverege/ui-elements';
import classnames from 'classnames';
import S from './Renderer.css';
import { ResourceImageViewerFactory } from "./Factory.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EMPTPY_COLLECTION = {};
export default class ResourceImageColumnRenderer extends AttributeColumnRenderer {
  renderCell = data => {
    // how should I save the images into the data, and as a resource?
    // just index? and the indexes can shuffle around? and just allow deletes to fall out?
    // use file name? or resource id? or just make a uuid?
    const {
      value,
      row,
      column: {
        attribute
      },
      context
    } = data;
    const {
      data: deviceData,
      type
    } = row.original;
    const {
      clientProps: {
        delegate
      }
    } = context;
    const nameAttribute = Attributes.getAttribute('name', type);
    const empty = this.column?.cell?.emptyValue;
    const emptyIcon = this.column?.cell?.emptyIcon;
    const buttonText = attribute.source.attribute.getString('viewImage', '');
    const titleText = attribute.source.attribute.getString('titleDialogImage', `${nameAttribute.get(deviceData)} ${attribute.displayName}`);
    const {
      objectFit,
      imageHeight,
      imageWidth
    } = this.column?.dialog || {};
    const imageStyle = {
      objectFit,
      width: imageWidth && `${imageWidth}px`,
      height: imageHeight && `${imageHeight}px`
    };
    const buttonVariant = this.column?.cell?.variant;
    const titleIcon = this.column?.dialog?.titleIcon;
    const alignItems = TableStyle.toFlexAlign(this.column?.cell?.align || 'baseline');
    const justifyItems = TableStyle.toFlexJustify(this.column?.cell?.justify || 'initial');
    const imagePathRoot = attribute.blueprint.attribute.getTagStartingWith('resourceImagePrefix:', true) || `resourceImage/${attribute?.blueprint?.updatePath.slice(5)}`;
    const viewerType = attribute.blueprint.attribute.getTagStartingWith('resourceImageViewer:', true);
    const clientProps = {
      delegate: delegate.item(row.id),
      attribute,
      renderer: this
    };
    return /*#__PURE__*/_jsx(ImageCellDefaultRenderer, {
      alignItems: alignItems,
      buttonText: buttonText,
      buttonVariant: buttonVariant,
      delegate: delegate,
      empty: empty,
      emptyIcon: emptyIcon,
      imagePathRoot: imagePathRoot,
      imageStyle: imageStyle,
      justifyItems: justifyItems,
      row: row,
      titleIcon: titleIcon,
      titleText: titleText,
      value: value || EMPTPY_COLLECTION,
      viewerType: viewerType,
      clientProps: clientProps
    });
  };
}
function ImageCellDefaultRenderer({
  alignItems,
  buttonText,
  buttonVariant,
  clientProps,
  delegate,
  empty,
  emptyIcon,
  imagePathRoot,
  imageStyle,
  justifyItems,
  row,
  titleIcon,
  titleText,
  value,
  viewerType
}) {
  const onOpen = useCallback(() => {
    Dialogs.show({
      component: DialogComponent,
      props: {
        clientProps,
        delegate: delegate.item(row.id),
        imagePathRoot,
        imageStyle,
        titleIcon,
        titleText,
        value,
        viewerType
      }
    });
  }, [titleIcon, titleText, delegate, row, imagePathRoot, imageStyle, value, viewerType, clientProps]);
  return value && Object.keys(value).length > 0 ? /*#__PURE__*/_jsx("div", {
    onContextMenu: evt => {
      evt.stopPropagation();
    },
    style: {
      display: 'flex',
      alignItems,
      flexWrap: 'nowrap',
      justifyItems
    },
    onDoubleClick: e => {
      e.stopPropagation();
      e.preventDefault();
    },
    children: /*#__PURE__*/_jsx(Button, {
      variant: buttonVariant,
      onClick: onOpen,
      children: !buttonText ? Object.keys(value).length : buttonText
    })
  }) : /*#__PURE__*/_jsx(Text, {
    icon: emptyIcon,
    children: empty
  });
}
function DialogComponent({
  clientProps,
  delegate,
  imagePathRoot,
  imageStyle,
  onClose,
  show,
  titleIcon,
  titleText,
  value,
  viewerType
}) {
  const [viewIndex, setViewIndex] = useState(0);
  const values = useMemo(() => {
    if (typeof value === 'object') {
      return Object.keys(value).sort((a, b) => {
        if (value[a] > value[b]) {
          return -1;
        }
        return 1;
      });
    }
    return null;
  }, [value]);
  const changeViewIndex = useCallback((index, change) => {
    if (change === 'inc') {
      if (index + 1 < values.length) {
        setViewIndex(index + 1);
      } else {
        setViewIndex(0);
      }
    } else if (change === 'dec') {
      if (index - 1 >= 0) {
        setViewIndex(index - 1);
      } else {
        setViewIndex(values.length - 1);
      }
    }
  }, [values]);
  const ImageRenderer = ResourceImageViewerFactory.get(viewerType);
  return /*#__PURE__*/_jsx(Dialog, {
    bodyVariant: "dialogBody",
    bodyClassName: classnames(S.noSize, S.win, S.really),
    title: /*#__PURE__*/_jsxs(Flex, {
      variant: "rowSpaceBetween",
      children: [/*#__PURE__*/_jsx(Text, {
        icon: titleIcon,
        variant: "dialogTitle",
        children: titleText
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        variant: "closeDialog"
      })]
    }),
    footer: /*#__PURE__*/_jsxs(Flex, {
      align: "center",
      justify: "center",
      variant: "rowL",
      children: [/*#__PURE__*/_jsx(Button, {
        icon: "fa-solid fa-chevron-left",
        onClick: () => {
          changeViewIndex(viewIndex, 'dec');
        },
        variant: "secondaryIconic"
      }), /*#__PURE__*/_jsxs(Text, {
        className: S.dialogText,
        children: [viewIndex + 1, " of ", values.length]
      }), /*#__PURE__*/_jsx(Button, {
        icon: "fa-solid fa-chevron-right",
        onClick: () => changeViewIndex(viewIndex, 'inc'),
        variant: "secondaryIconic"
      })]
    }),
    show: show,
    onClose: onClose,
    children: /*#__PURE__*/_jsx(Flex, {
      align: "center",
      justify: "center",
      children: /*#__PURE__*/_jsx(ImageRenderer, {
        value: values[viewIndex],
        path: `${imagePathRoot}/${values[viewIndex]}`,
        valueStyle: imageStyle,
        clientProps: clientProps
      })
    })
  });
}